/**
 * Remark plugin: remarkSingleCard
 *
 * Transforms emoji-prefixed blockquotes into styled card nodes.
 * Detects card types (win/warning/quote/note) based on emoji and label patterns.
 *
 * This plugin works in coordination with remarkGroupedCards:
 * - Skips blockquotes already inside grouped_cards nodes
 * - Processes standalone blockquotes with card indicators
 *
 * @example
 * Input markdown:
 * ```markdown
 * > ✅ **Win:** Closed 3 deals this week
 * > ⚠️ **Warning:** Pipeline needs attention
 * > 💬 **Quote:** "Success is a journey, not a destination"
 * > 📝 **Note:** Follow up with client tomorrow
 * ```
 *
 * Output mdast:
 * ```typescript
 * {
 *   type: 'card',
 *   cardType: 'win',
 *   children: [...blockquote children...],
 *   data: {
 *     hName: 'div',
 *     hProperties: {
 *       className: ['card-block', 'card-win'],
 *       'data-card-type': 'win'
 *     }
 *   }
 * }
 * ```
 */

import type { Blockquote, Parent, Root, Text } from 'mdast';
import { CONTINUE, SKIP, visit } from 'unist-util-visit';
import type { CardNode, CardType } from './types';
import { detectCardType } from './types';

/**
 * Extract text content from a blockquote node
 *
 * Recursively walks the blockquote tree to concatenate all text nodes
 */
function extractTextContent(node: Parent | Text): string {
  if (node.type === 'text') {
    return (node as Text).value;
  }

  if ('children' in node && Array.isArray(node.children)) {
    return node.children.map((child) => extractTextContent(child as Parent | Text)).join(' ');
  }

  return '';
}

/**
 * Create a card mdast node from a blockquote
 */
function createCardNode(blockquote: Blockquote, cardType: CardType): CardNode {
  return {
    type: 'card',
    cardType,
    children: blockquote.children,
    data: {
      hName: 'div',
      hProperties: {
        className: ['card-block', `card-${cardType}`],
        'data-card-type': cardType,
      },
    },
  };
}

/**
 * Remark plugin to transform emoji-prefixed blockquotes into card nodes
 */
export default function remarkSingleCard() {
  return (tree: Root) => {
    visit(tree, 'blockquote', (node: Blockquote, index, parent) => {
      // Safety check: ensure we have parent and index
      if (!parent || index === undefined) {
        return CONTINUE;
      }

      // NOTE: We DO process blockquotes inside grouped_cards!
      // They need to be transformed into card nodes to get proper styling.
      // The grouped_cards node accepts (card | blockquote)+ children.

      // Extract text content from blockquote
      const textContent = extractTextContent(node);

      // Detect card type from content
      const cardType = detectCardType(textContent);

      // If not a card, leave blockquote as-is
      if (!cardType) {
        return CONTINUE;
      }

      // Transform blockquote into card node
      const cardNode = createCardNode(node, cardType);

      // Replace blockquote with card node
      parent.children[index] = cardNode as any;

      // Skip the newly created node to avoid re-processing
      return [SKIP, index];
    });
  };
}
